import React, { useEffect, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import { Tag } from 'primereact/tag'

import 'primereact/resources/themes/lara-dark-indigo/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'
import './Members.css'

import useOrgJwt from '../hooks/useOrgJwt'
import { formatDate } from '../utils/dateFormatter'

const API_BASE = process.env.REACT_APP_API_BASE_URL || ''

const ROLES = [
  { label: 'Admin', value: 'admin' },
  { label: 'Member', value: 'viewer' }
]

export default function Members() {
  const toastRef = useRef(null)
  const { isSignedIn, user } = useUser()
  const { orgJwt, tenantId, role, loading } = useOrgJwt()

  const [orgName, setOrgName] = useState('')
  const [members, setMembers] = useState([])
  const [busyRow, setBusyRow] = useState({})
  const [invites, setInvites] = useState([])

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteBusy, setInviteBusy] = useState(false)

  const isAdmin = role === 'admin'
  const currentUserId = user?.id

  useEffect(() => {
    if (!isSignedIn || loading || !tenantId || !orgJwt) return
    loadOrgName(tenantId)
  }, [isSignedIn, loading, tenantId, orgJwt])

  useEffect(() => {
    if (!isSignedIn || loading || !tenantId || !orgJwt) return
    loadMembers()
    loadInvites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, loading, tenantId, orgJwt])

  const authz = () => ({ Authorization: `Bearer ${orgJwt}` })

  const loadOrgName = async (tid) => {
    if (!orgJwt) return
    try {
      const res = await fetch(`${API_BASE}/organizations/${tid}`, { headers: authz() })
      if (!res.ok) return
      const j = await res.json()
      setOrgName(j?.name || '')
    } catch { /* ignore */ }
  }

  const loadMembers = async () => {
    try {
      const res = await fetch(`${API_BASE}/organization-users`, { headers: authz() })
      if (!res.ok) throw new Error('Failed to load members')
      const data = await res.json()
      const arr = Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : [])
      const normalized = arr.map(x => ({
        ...x,
        id: x.id || x.user_id || x.clerk_user_id || x.email,
        _editing: false,
        _originalRole: null
      }))
      setMembers(normalized)
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    }
  }

  const loadInvites = async () => {
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/invites`, { headers: authz() })
      if (!res.ok) { setInvites([]); return }
      const raw = await res.json()
      const arr = Array.isArray(raw) ? raw : (raw.invites || [])
      const normalized = arr.map(x => ({ ...x, email_canonical: x.email_canonical || x.email }))
      const pending = normalized.filter(x => String(x.status).toLowerCase() === 'pending')
      setInvites(pending)
    } catch {
      setInvites([])
    }
  }

  const enableEdit = (row) => {
    const key = row.user_id || row.id
    if (key === currentUserId) return
    setMembers(ms => ms.map(m => {
      if ((m.user_id || m.id) !== key) return m
      if (m._editing) return m
      return { ...m, _editing: true, _originalRole: m.role }
    }))
  }

  const cancelEdit = (row) => {
    const key = row.user_id || row.id
    setMembers(ms => ms.map(m => {
      if ((m.user_id || m.id) !== key) return m
      const revertRole = m._originalRole ?? m.role
      return { ...m, role: revertRole, _editing: false, _originalRole: null }
    }))
  }

  const onRoleChange = (row, newRole) => {
    const key = row.user_id || row.id
    setMembers(ms => ms.map(m => ((m.user_id || m.id) === key ? { ...m, role: newRole } : m)))
  }

  const saveRole = async (row) => {
    const key = row.user_id || row.id
    try {
      setBusyRow(prev => ({ ...prev, [key]: true }))
      const res = await fetch(`${API_BASE}/organization-users/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authz() },
        body: JSON.stringify({ role: row.role })
      })
      if (!res.ok) throw new Error('Failed to update role')
      toastRef.current?.show({ severity: 'success', summary: 'Saved', detail: 'Role updated' })
      setMembers(ms => ms.map(m => ((m.user_id || m.id) === key ? { ...m, _editing: false, _originalRole: null } : m)))
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    } finally {
      setBusyRow(prev => ({ ...prev, [key]: false }))
    }
  }

  const removeMember = async (row) => {
    const uid = row.user_id || row.id
    if (uid === currentUserId) {
      toastRef.current?.show({ severity: 'warn', summary: 'Cannot remove yourself' })
      return
    }
    const ok = window.confirm(`Remove ${row.email || uid} from the organization?`)
    if (!ok) return
    try {
      const res = await fetch(`${API_BASE}/organization-users/${uid}`, {
        method: 'DELETE',
        headers: authz()
      })
      if (!res.ok) {
        let detail = 'Failed to remove user'
        try { const j = await res.json(); if (j?.error) detail = j.error } catch {}
        throw new Error(detail)
      }
      toastRef.current?.show({ severity: 'success', summary: 'Removed' })
      loadMembers()
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    }
  }

  const invite = async () => {
    if (!inviteEmail.trim()) {
      toastRef.current?.show({ severity: 'warn', summary: 'Email required' })
      return
    }
    try {
      setInviteBusy(true)
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authz() },
        body: JSON.stringify({ Email: inviteEmail.trim() })
      })
      if (!res.ok) throw new Error('Failed to create invite')
      setInviteOpen(false)
      setInviteEmail('')
      toastRef.current?.show({ severity: 'success', summary: 'Invite sent' })
      loadInvites()
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    } finally {
      setInviteBusy(false)
    }
  }

  const resendInvite = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/invites/${row.id}/resend`, {
        method: 'POST',
        headers: authz()
      })
      if (!res.ok) throw new Error('Failed to resend')
      toastRef.current?.show({ severity: 'success', summary: 'Resent', detail: 'Invite extended 7 days' })
      loadInvites()
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    }
  }

  const revokeInvite = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/invites/${row.id}/revoke`, {
        method: 'POST',
        headers: authz()
      })
      if (!res.ok) throw new Error('Failed to revoke')
      toastRef.current?.show({ severity: 'success', summary: 'Revoked' })
      loadInvites()
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    }
  }

  const copyInviteLink = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/invites/${row.id}/resend`, {
        method: 'POST',
        headers: authz()
      })
      if (!res.ok) throw new Error('Failed to get link')
      const { token } = await res.json()
      const base = process.env.REACT_APP_APP_BASE_URL || window.location.origin
      const url = `${base}/invites/accept?token=${token}`
      await navigator.clipboard.writeText(url)
      toastRef.current?.show({ severity: 'success', summary: 'Copied', detail: 'Invite link copied' })
    } catch (e) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: e.message })
    }
  }

  const statusTag = (s) => {
    const map = {
      pending: { severity: 'info', label: 'Pending' },
      accepted: { severity: 'success', label: 'Accepted' },
      revoked: { severity: 'danger', label: 'Revoked' },
      expired: { severity: 'warning', label: 'Expired' }
    }
    const m = map[(s || '').toLowerCase()] || { severity: 'secondary', label: s }
    return <Tag severity={m.severity} value={m.label} />
  }

  if (loading) return null

  return (
    <div className="members-page">
      <Toast ref={toastRef} />

      {orgName && <h1 className="text-3xl font-semibold mb-6 text-center">{orgName}</h1>}

      <div className="flex items-center justify-between mb-4 members-row">
        <h2 className="text-2xl font-semibold">Members</h2>
        {isAdmin && (
          <Button
            label="Invite by email"
            icon="pi pi-user-plus"
            className="p-button-rounded"
            onClick={() => setInviteOpen(true)}
          />
        )}
      </div>

      <div className="card members-card rounded-2xl shadow-2">
        <DataTable value={members} className="p-datatable-sm members-table" stripedRows showGridlines>
          <Column field="email" header="Email" />
          <Column header="Role" body={(row) => {
            const key = row.user_id || row.id
            const isSelf = key === currentUserId
            return (
              <div className="flex items-center gap-2">
                <Dropdown
                  value={row.role}
                  options={ROLES}
                  onChange={(e) => onRoleChange(row, e.value)}
                  disabled={!isAdmin || isSelf || !row._editing}
                  className="w-12rem"
                />
                {isAdmin && !isSelf && !row._editing && (
                  <Button icon="pi pi-pencil" text onClick={() => enableEdit(row)} aria-label="Edit role" />
                )}
                {isAdmin && row._editing && (
                  <div className="flex gap-2">
                    <Button icon="pi pi-times" text onClick={() => cancelEdit(row)} aria-label="Cancel" />
                    <Button
                      icon={busyRow[key] ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
                      text
                      onClick={() => saveRole(row)}
                      aria-label="Save"
                    />
                  </div>
                )}
              </div>
            )
          }} />
          {isAdmin && (
            <Column header="Actions" body={(row) => {
              const isSelf = (row.user_id || row.id) === currentUserId
              return (
                <div className="flex gap-2">
                  <Button
                    label="Remove"
                    icon="pi pi-user-minus"
                    text
                    className="p-button-danger"
                    disabled={isSelf}
                    onClick={() => removeMember(row)}
                  />
                </div>
              )
            }} />
          )}
        </DataTable>
      </div>

      <h3 className="text-xl font-semibold mt-8 mb-3 text-center">Pending invites</h3>
      <div className="card invites-card rounded-2xl shadow-2">
        <DataTable
          value={invites}
          className="p-datatable-sm members-table"
          stripedRows
          showGridlines
          emptyMessage="No pending invites — send one to grow your team!"
        >
          <Column field="email_canonical" header="Email" />
          <Column header="Created" body={(row) => formatDate(row.created_at, 'table')} />
          <Column header="Expires" body={(row) => formatDate(row.expires_at, 'table')} />
          <Column header="Status" body={(row) => statusTag(row.status)} />
          <Column
            header="Actions"
            body={(row) => (
              <div className="flex gap-2">
                <Button label="Copy link" icon="pi pi-link" text onClick={() => copyInviteLink(row)} />
                <Button label="Resend" icon="pi pi-send" text onClick={() => resendInvite(row)} />
                <Button label="Revoke" icon="pi pi-ban" text onClick={() => revokeInvite(row)} />
              </div>
            )}
          />
        </DataTable>
      </div>

      <Dialog
        header="Invite by email"
        visible={inviteOpen}
        onHide={() => setInviteOpen(false)}
        style={{ width: '30rem' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" text onClick={() => setInviteOpen(false)} />
            <Button
              label={inviteBusy ? 'Sending…' : 'Send invite'}
              icon={inviteBusy ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
              onClick={invite}
              disabled={inviteBusy}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <label htmlFor="inviteEmail" className="block mb-2">Email</label>
          <InputText
            id="inviteEmail"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="name@company.com"
          />
          <p className="mt-3 text-sm opacity-80">
            The recipient will get a one-time link that expires in 7 days.
          </p>
        </div>
      </Dialog>
    </div>
  )
}
